/**
 * Export Options Modal
 * Allows users to configure export settings before downloading
 */

import { useState } from 'react'
import './ExportOptionsModal.css'

export interface ExportOptions {
  format: 'png' | 'svg' | 'json' | 'pdf'
  backgroundColor: 'white' | 'transparent'
  padding: number
  quality: number
  exportMode?: 'ai' | 'full'
  jsonAction?: 'download' | 'copy'
}

interface ExportOptionsModalProps {
  isOpen: boolean
  onClose: () => void
  onExport: (options: ExportOptions) => void
  defaultFormat?: 'png' | 'svg' | 'json' | 'pdf'
}

export default function ExportOptionsModal({
  isOpen,
  onClose,
  onExport,
  defaultFormat = 'png'
}: ExportOptionsModalProps) {
  const [format, setFormat] = useState<'png' | 'svg' | 'json' | 'pdf'>(defaultFormat)
  const [backgroundColor, setBackgroundColor] = useState<'white' | 'transparent'>('white')
  const [padding, setPadding] = useState<number>(20)
  const [quality, setQuality] = useState<number>(3)
  const [exportMode, setExportMode] = useState<'ai' | 'full'>('full')
  const [jsonAction, setJsonAction] = useState<'download' | 'copy'>('download')

  if (!isOpen) return null

  const handleExport = () => {
    onExport({
      format,
      backgroundColor,
      padding,
      quality,
      exportMode: format === 'json' ? exportMode : undefined,
      jsonAction: format === 'json' ? jsonAction : undefined
    })
    onClose()
  }

  return (
    <div className="export-modal-overlay" onClick={onClose}>
      <div className="export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="export-modal-header">
          <h2>Export Options</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="export-modal-body">
          {/* Format Selection */}
          <div className="export-option-group">
            <label>Format</label>
            <div className="format-buttons">
              <button
                className={`format-btn ${format === 'png' ? 'active' : ''}`}
                onClick={() => setFormat('png')}
              >
                PNG
              </button>
              <button
                className={`format-btn ${format === 'svg' ? 'active' : ''}`}
                onClick={() => setFormat('svg')}
              >
                SVG
              </button>
              <button
                className={`format-btn ${format === 'pdf' ? 'active' : ''}`}
                onClick={() => setFormat('pdf')}
              >
                PDF
              </button>
              <button
                className={`format-btn ${format === 'json' ? 'active' : ''}`}
                onClick={() => setFormat('json')}
              >
                JSON
              </button>
            </div>
          </div>

          {/* Background Color (only for PNG/SVG) */}
          {(format === 'png' || format === 'svg' || format === 'pdf') && (
            <div className="export-option-group">
              <label>Background</label>
              <div className="bg-options">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="background"
                    value="white"
                    checked={backgroundColor === 'white'}
                    onChange={() => setBackgroundColor('white')}
                  />
                  <span>White</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="background"
                    value="transparent"
                    checked={backgroundColor === 'transparent'}
                    onChange={() => setBackgroundColor('transparent')}
                  />
                  <span>Transparent</span>
                </label>
              </div>
            </div>
          )}

          {/* Padding (only for PNG/SVG) */}
          {(format === 'png' || format === 'svg' || format === 'pdf') && (
            <div className="export-option-group">
              <label>Padding: {padding}px</label>
              <input
                type="range"
                min="0"
                max="100"
                value={padding}
                onChange={(e) => setPadding(Number(e.target.value))}
                className="slider"
              />
              <div className="slider-labels">
                <span>None</span>
                <span>More</span>
              </div>
            </div>
          )}

          {/* Quality (only for PNG / PDF) */}
          {(format === 'png' || format === 'pdf') && (
            <div className="export-option-group">
              <label>Quality: {quality}x</label>
              <input
                type="range"
                min="1"
                max="5"
                step="1"
                value={quality}
                onChange={(e) => setQuality(Number(e.target.value))}
                className="slider"
              />
              <div className="slider-labels">
                <span>1x (Fast)</span>
                <span>5x (Ultra Sharp)</span>
              </div>
              <p className="help-text">
                Higher quality = sharper image but larger file size
              </p>
            </div>
          )}

          {/* Export Mode (only for JSON) */}
          {format === 'json' && (
            <>
              <div className="export-option-group">
                <label>Export Mode</label>
                <div className="format-buttons">
                  <button
                    className={`format-btn ${exportMode === 'ai' ? 'active' : ''}`}
                    onClick={() => setExportMode('ai')}
                  >
                    AI-Friendly
                  </button>
                  <button
                    className={`format-btn ${exportMode === 'full' ? 'active' : ''}`}
                    onClick={() => setExportMode('full')}
                  >
                    Full
                  </button>
                </div>
                {exportMode === 'ai' ? (
                  <p className="help-text">
                    📦 <strong>Minimal JSON:</strong> Preserves templates & patterns, only expands edited nodes.
                    Includes edit tracking metadata for AI refinement. ~70% smaller!
                  </p>
                ) : (
                  <p className="help-text">
                    📄 <strong>Complete JSON:</strong> All nodes & edges fully expanded.
                    Perfect for archiving or manual editing. Lossless export.
                  </p>
                )}
              </div>

              <div className="export-option-group">
                <label>Action</label>
                <div className="format-buttons">
                  <button
                    className={`format-btn ${jsonAction === 'download' ? 'active' : ''}`}
                    onClick={() => setJsonAction('download')}
                  >
                    💾 Download
                  </button>
                  <button
                    className={`format-btn ${jsonAction === 'copy' ? 'active' : ''}`}
                    onClick={() => setJsonAction('copy')}
                  >
                    📋 Copy to Clipboard
                  </button>
                </div>
                {jsonAction === 'download' ? (
                  <p className="help-text">
                    Download JSON file to your computer
                  </p>
                ) : (
                  <p className="help-text">
                    Copy JSON to clipboard for pasting into AI or code editor
                  </p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="export-modal-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button className="export-btn" onClick={handleExport}>
            {format === 'json' && jsonAction === 'copy'
              ? `Copy ${exportMode === 'ai' ? 'AI' : 'Full'} JSON`
              : `Export ${format.toUpperCase()}`
            }
          </button>
        </div>
      </div>
    </div>
  )
}
